import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";

/**
 * Part identity — a stable id on every top-level block.
 *
 * The point is revision capture: with ids, the server can diff and merge **parts** rather than
 * positions, so two people editing different paragraphs of the same document do not collide
 * just because one of them inserted a line above the other. Without them, "the third block" is
 * the only way to name a block, and that name changes every time anything above it does.
 *
 * The rules that make an id trustworthy:
 *   - a new top-level node gets a fresh id;
 *   - a SPLIT produces a new node, which gets a fresh id (both halves cannot be the same part);
 *   - a MOVE carries its id — that is the whole point;
 *   - copy/paste duplicates an id, so the duplicate is reassigned. A document with two blocks
 *     claiming the same identity is worse than one with none.
 *
 * ⚠ This covers the markdown (Tiptap) lane only. `editor-core`'s schema declares `partId` on
 * every block but has NO plugin that assigns it, so the v2 lane's parts are all null and a
 * part-level merge cannot work there yet. That gap belongs to the engine, not to this file,
 * and is recorded as a dependency for the merge wave rather than fixed by widening this one.
 */

export const PART_ID_ATTRIBUTE = "partId";
const partIdKey = new PluginKey("docit-part-id");

/** Which node types carry a part id. Atoms included — a widget is as much a part as a paragraph. */
export interface PartIdOptions {
  types: string[];
}

let counter = 0;

/**
 * A fresh id. `crypto.randomUUID` where it exists; otherwise a monotonic fallback, because a
 * non-unique id is the one failure mode this extension cannot have — and `Math.random` in a
 * loop is exactly how a collision happens.
 */
export function freshPartId(): string {
  const cryptoRef = globalThis.crypto as { randomUUID?: () => string } | undefined;
  if (typeof cryptoRef?.randomUUID === "function") {
    return cryptoRef.randomUUID();
  }
  counter += 1;
  return `part-${Date.now().toString(36)}-${counter.toString(36)}`;
}

export const PartIdentity = Extension.create<PartIdOptions>({
  name: "partIdentity",

  addOptions() {
    return {
      types: [
        "paragraph",
        "heading",
        "blockquote",
        "codeBlock",
        "bulletList",
        "orderedList",
        "horizontalRule",
        "widget",
        "chart",
        "conversation",
        "instruction_block",
      ],
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          [PART_ID_ATTRIBUTE]: {
            default: null,
            parseHTML: (element) => element.getAttribute("data-part-id"),
            renderHTML: (attributes) => {
              const value = attributes[PART_ID_ATTRIBUTE];
              return value ? { "data-part-id": String(value) } : {};
            },
            // Part ids are structure, not content: they must never appear in the markdown
            // projection, which is the document's interchange format.
            keepOnSplit: false,
          },
        },
      },
    ];
  },

  addProseMirrorPlugins() {
    const types = new Set(this.options.types);
    return [
      new Plugin({
        key: partIdKey,
        /**
         * One pass over the top level after every content-changing transaction. Cheap because
         * it only looks at direct children of the doc, and correct because it runs AFTER the
         * change — a split's second half is already its own node by the time this sees it.
         */
        appendTransaction: (transactions, _oldState, newState) => {
          if (!transactions.some((tr) => tr.docChanged)) {
            return null;
          }
          const seen = new Set<string>();
          const fixes: Array<{ pos: number; id: string }> = [];

          newState.doc.forEach((node, offset) => {
            if (!types.has(node.type.name)) {
              return;
            }
            const current = node.attrs[PART_ID_ATTRIBUTE];
            const id = typeof current === "string" && current.length > 0 ? current : null;
            // Missing, or a duplicate left behind by a paste — either way, a fresh identity.
            if (!id || seen.has(id)) {
              fixes.push({ pos: offset, id: freshPartId() });
              return;
            }
            seen.add(id);
          });

          if (fixes.length === 0) {
            return null;
          }
          const tr = newState.tr;
          for (const fix of fixes) {
            const node = newState.doc.nodeAt(fix.pos);
            if (node) {
              tr.setNodeMarkup(fix.pos, undefined, {
                ...node.attrs,
                [PART_ID_ATTRIBUTE]: fix.id,
              });
            }
          }
          // Not undoable on its own: a user pressing ⌘Z should undo their edit, not an id.
          tr.setMeta("addToHistory", false);
          tr.setMeta(partIdKey, true);
          return tr;
        },
      }),
    ];
  },
});
